import { UserType } from 'shared/src/domain/user/user-types';

export const clientSuccessDirectorRoles = 'Client Success Director';
export const leadAccountManagerRoles = 'Lead Account Manager';
export const seniorMediaTraderRoles = 'Senior Media Trader';
export const adOpsRoles = ['Ad Ops Supervisor', 'Ad Ops Specialist'];
export const analyticsRoles = ['Tag Specialist'];
export const mediaTradersRoles = [
  'Media Trader',
  'Senior Media Trader',
  'Associate Trader',
  'SEM Specialist',
  'Senior SEM Specialist'
];

export type EmployeeRole =
  | typeof clientSuccessDirectorRoles
  | typeof leadAccountManagerRoles
  | typeof seniorMediaTraderRoles
  | (typeof adOpsRoles)[number]
  | (typeof analyticsRoles)[number]
  | (typeof mediaTradersRoles)[number];

/** "Media Trader" -> "MT", "Client Success Director" -> "CSD". Drops any
 *  " - " qualifier and keeps existing acronyms (e.g. "SEM") whole. */
export function roleInitials(role: string): string {
  const base = role.split(' - ')[0].trim();
  const letters = base
    .split(/\s+/)
    .map(word => (/^[A-Z]{2,}$/.test(word) ? word : (word[0] ?? '')))
    .join('');
  return letters.slice(0, 3).toUpperCase();
}

// Deterministic palette index so the same role reads the same colour in every switcher.
export function roleColorIndex(role: string, paletteSize: number): number {
  let hash = 0;
  for (let i = 0; i < role.length; i++) hash = (hash * 31 + role.charCodeAt(i)) | 0;
  return Math.abs(hash) % paletteSize;
}

export function filterUsersBySearch<T extends { name: string; email: string; roles: string[] }>(
  users: T[],
  query: string
): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return users;
  return users.filter(
    u =>
      u.name.toLowerCase().includes(needle) ||
      u.email.toLowerCase().includes(needle) ||
      u.roles.some(r => r.toLowerCase().includes(needle))
  );
}

export function filterSeniorMediaTraders(users: UserType[] | null) {
  if (!users) return [];
  return users.filter(user => seniorMediaTraderRoles.includes(user.rep_type));
}

export function filterClientSuccessDirectors(users: UserType[] | null) {
  if (!users) return [];
  return users.filter(user => clientSuccessDirectorRoles.includes(user.rep_type));
}

export function filterAnalysts(users: UserType[] | null) {
  if (!users) return [];
  return users.filter(user => analyticsRoles.includes(user.rep_type));
}

export function filterAdOps(users: UserType[] | null) {
  if (!users) return [];
  return users.filter(user => adOpsRoles.includes(user.rep_type));
}
